import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Button,
  SettingIcon,
  Tab,
  Tabs,
  Popover,
  Switch,
  Typography,
  Input,
  Checkbox,
  NavigateNextIcon
} from '@attentive-platform/stem-ui';
import { ROSETTA_TAB_ENUM } from '../../../Constants/Constants';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { ScopeSelectionIcon, QuotePricingIcon, ResetIcon } from '../rosettaSvgs';

const SettingsDropdown = () => {
  const { showPricing, scopeMode, inflationPercentage, volumeDiscountTiers, showTiersTable, hiddenTiers, dispatch } =
    useRosettaStore(
      useShallow(state => ({
        showPricing: state.showPricing,
        scopeMode: state.scopeMode,
        inflationPercentage: state.inflationPercentage,
        volumeDiscountTiers: state.volumeDiscountTiers,
        showTiersTable: state.showTiersTable,
        hiddenTiers: state.hiddenTiers,
        dispatch: state.dispatch
      }))
    );

  const handleScopeModeToggle = event => {
    dispatch({ type: 'SET_SCOPE_MODE', payload: event.target.checked });
    if (event.target.checked && showPricing) {
      dispatch({ type: 'SET_SHOW_PRICING', payload: false });
    }
  };

  const handleInflationChange = event => {
    const value = event.target.value;
    if (value === '') {
      dispatch({ type: 'SET_INFLATION_PERCENTAGE', payload: 0 });
      return;
    }
    const inflationValue = parseFloat(value);
    if (!isNaN(inflationValue) && inflationValue >= 0) {
      dispatch({ type: 'SET_INFLATION_PERCENTAGE', payload: inflationValue });
    }
  };

  const handleShowTableToggle = event => {
    dispatch({ type: 'SET_SHOW_TIERS_TABLE', payload: event.target.checked });
  };

  const handleTierVisibilityToggle = tierSheetCount => {
    dispatch({ type: 'TOGGLE_TIER_VISIBILITY', payload: tierSheetCount });
  };

  const tiersList = Object.keys(volumeDiscountTiers);

  const tiersVisibilityContent = (
    <div className='min-w-[140px] p-3'>
      <div className='font-semibold text-gray-500'>Visibility Settings</div>
      <div className='flex items-center gap-2 mb-2'>
        <Checkbox checked={showTiersTable} label='Show Table' onChange={handleShowTableToggle} size='small' />
      </div>

      <div className='font-semibold text-gray-500'>Hide Specific Tiers</div>

      {tiersList.map(sheetCount => {
        const tierKey = parseInt(sheetCount, 10);
        return (
          <div key={sheetCount}>
            <Checkbox
              size='small'
              checked={!hiddenTiers.has(tierKey)}
              label={`${tierKey.toLocaleString()}+ Sheets`}
              onChange={() => handleTierVisibilityToggle(sheetCount)}
            />
          </div>
        );
      })}
    </div>
  );

  const dropdownContent = (
    <div className='max-w-[190px]'>
      <div className='flex items-center justify-between gap-4 p-3'>
        <Typography className='mr-4'>Scope Mode</Typography>
        <Switch size='small' checked={scopeMode} onChange={handleScopeModeToggle} />
      </div>
      <Popover
        triggerEvent='hover'
        content={tiersVisibilityContent}
        popoverProps={{ placement: 'bottom-end' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
        <div className='flex items-center gap-1 p-3 cursor-pointer hover:bg-gray-50'>
          <Typography>Tiers Visibility</Typography>
          <NavigateNextIcon />
        </div>
      </Popover>
      <div className='flex items-center justify-between gap-4 p-3'>
        <Typography>Calibration</Typography>
        <Input
          size='small'
          type='number'
          className='w-[50px]'
          value={inflationPercentage}
          onChange={handleInflationChange}
        />
      </div>
    </div>
  );

  return (
    <Popover
      triggerEvent='click'
      content={dropdownContent}
      popoverProps={{ placement: 'bottom-end' }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
      <SettingIcon fill='#4461ed' className='cursor-pointer ml-4' />
    </Popover>
  );
};

const RosettaHeader = ({ onReset }) => {
  const { showPricing, scopeMode, dispatch } = useRosettaStore(
    useShallow(state => ({
      showPricing: state.showPricing,
      scopeMode: state.scopeMode,
      dispatch: state.dispatch
    }))
  );

  const handleTabChange = (event, newValue) => {
    dispatch({ type: 'SET_SHOW_PRICING', payload: newValue === ROSETTA_TAB_ENUM.QUOTE_PRICING });
  };

  return (
    <header className='rosetta-header'>
      <div className='rosetta-header-content'>
        <div className='rosetta-logo'>
          <h1>Rosetta: Beam AI</h1>
          <span className='rosetta-subtitle'>Scope & Pricing Configurator</span>
        </div>
        <div className='rosetta-tab-navigation'>
          <Tabs
            value={showPricing ? ROSETTA_TAB_ENUM.QUOTE_PRICING : ROSETTA_TAB_ENUM.SCOPE_SELECTION}
            onChange={handleTabChange}
            textColor='primary'
            indicatorColor='primary'>
            <Tab
              value={ROSETTA_TAB_ENUM.SCOPE_SELECTION}
              label={
                <div className='flex items-center gap-1'>
                  <ScopeSelectionIcon />
                  <span>Scope Selection</span>
                </div>
              }
            />
            {!scopeMode && (
              <Tab
                className='ml-2'
                value={ROSETTA_TAB_ENUM.QUOTE_PRICING}
                label={
                  <div className='flex items-center gap-1'>
                    <QuotePricingIcon />
                    <span>Quote & Pricing</span>
                  </div>
                }
              />
            )}
          </Tabs>
        </div>
        <div>
          <Button variant='outlined' onClick={onReset}>
            <ResetIcon />
            Reset
          </Button>
          <SettingsDropdown />
        </div>
      </div>
    </header>
  );
};

export default RosettaHeader;
